<div class="role-list-page min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6 overflow-hidden">
  <!-- Header -->
  <div class="mb-6 md:mb-8">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
          {{ 'sideMenu.roleAndPermission' | translate }}
        </h1>
        <p class="text-gray-600 text-sm md:text-base">
          Manage roles and module access for your organization
        </p>
      </div>
      <div class="flex flex-wrap gap-3 shrink-0">
        <div class="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <p class="text-sm text-gray-600">Total roles</p>
              <p class="text-lg font-semibold text-gray-900">{{ dataSource.data.length }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Toolbar -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 md:p-6 mb-6 md:mb-8">
    <div class="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
      <div class="flex-1 min-w-0">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-900 placeholder:text-gray-400"
            placeholder="Search roles by name, description, or permissions…"
            (input)="applyFilter($event)"
          />
        </div>
      </div>
      <a
        routerLink="/home/add-role-and-permission"
        class="inline-flex items-center justify-center gap-2 px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg shrink-0"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        <span>+ {{ 'ROLE' | translate }}</span>
      </a>
    </div>
  </div>

  <!-- Table card -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
    <div class="px-5 md:px-6 py-4 border-b border-gray-200 bg-gray-50">
      <h2 class="text-lg font-semibold text-gray-900">Roles overview</h2>
      <p class="text-sm text-gray-600 mt-0.5">View and maintain role definitions</p>
    </div>

    <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-16">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
      <p class="text-gray-600 text-sm">Loading roles…</p>
    </div>

    <div *ngIf="!isLoading && dataSource.filteredData.length > 0" class="overflow-x-auto">
      <table mat-table [dataSource]="dataSource" matSort class="role-table w-full min-w-[720px]">
        <ng-container matColumnDef="index">
          <th mat-header-cell *matHeaderCellDef class="role-th">#</th>
          <td mat-cell *matCellDef="let role; let i = index" class="role-td text-gray-600 tabular-nums">
            {{ displayRowNumber(i) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="role_name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="role-th">{{ 'roleName' | translate }}</th>
          <td mat-cell *matCellDef="let role" class="role-td">
            <span class="font-medium text-gray-900">{{ role.role_name }}</span>
          </td>
        </ng-container>

        <ng-container matColumnDef="description">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="role-th">{{ 'DESCRIPTION' | translate }}</th>
          <td mat-cell *matCellDef="let role" class="role-td max-w-xs md:max-w-md">
            <span class="text-sm text-gray-700 line-clamp-2" [title]="role.description || 'N/A'">
              {{ role.description || 'N/A' }}
            </span>
          </td>
        </ng-container>

        <ng-container matColumnDef="permissions_summary">
          <th mat-header-cell *matHeaderCellDef class="role-th">{{ 'Permissions' }}</th>
          <td mat-cell *matCellDef="let role" class="role-td">
            <div class="flex items-center gap-2 flex-wrap">
              <span
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border"
                [ngClass]="permissionBadgeClass(role)"
                [matTooltip]="getPermissionsSummary(role)"
                matTooltipShowDelay="200"
              >
                {{ getPermissionCount(role) }} {{ getPermissionCount(role) === 1 ? 'module' : 'modules' }}
              </span>
              <button
                type="button"
                class="inline-flex items-center justify-center w-8 h-8 rounded-full text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-100 transition-colors"
                (click)="viewRoleDetails(role)"
                [matTooltip]="getPermissionsSummary(role)"
                matTooltipShowDelay="200"
              >
                <mat-icon class="!text-lg">info_outline</mat-icon>
              </button>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="created_at">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="role-th">{{ 'DATE' | translate }}</th>
          <td mat-cell *matCellDef="let role" class="role-td whitespace-nowrap text-sm text-gray-800">
            {{ role.created_at | date:'MMM d, y' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="role-th text-right">{{ 'action' | translate }}</th>
          <td mat-cell *matCellDef="let role" class="role-td text-right whitespace-nowrap">
            <div class="inline-flex items-center gap-1 justify-end">
              <button
                mat-icon-button
                type="button"
                class="!text-blue-600 hover:!bg-blue-50"
                [disabled]="!canEdit"
                (click)="editRole(role)"
                [matTooltip]="canEdit ? 'Edit role' : 'No permission to edit'"
              >
                <mat-icon>edit</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                color="warn"
                class="!text-red-600 hover:!bg-red-50"
                [disabled]="!canDelete"
                (click)="deleteRole(role.role_id || role.id)"
                [matTooltip]="canDelete ? 'Delete role' : 'No permission to delete'"
              >
                <mat-icon>delete_outline</mat-icon>
              </button>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns" class="role-header-row"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="role-data-row hover:bg-gray-50/90 transition-colors"></tr>
      </table>
    </div>

    <app-empty-data
      *ngIf="!isLoading && dataSource.filteredData.length === 0"
      class="flex justify-center items-center py-10"
      [imageSource]="'../../assets/blankScreen/support.png'"
      [message]="'No data available'"
    ></app-empty-data>

    <!-- Pagination bar -->
    <div *ngIf="!isLoading && dataSource.filteredData.length > 0" class="role-paginator-wrap px-4 md:px-6 py-4 border-t border-gray-200 bg-gray-50">
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        <p class="text-sm text-gray-600 order-2 sm:order-1">
          <span *ngIf="paginator">
            Showing
            <span class="font-medium text-gray-800">{{ rangeStart }}</span>
            –
            <span class="font-medium text-gray-800">{{ rangeEnd }}</span>
            of
            <span class="font-medium text-gray-800">{{ dataSource.filteredData.length }}</span>
            roles
          </span>
        </p>
        <mat-paginator
          class="role-paginator order-1 sm:order-2 bg-transparent"
          [length]="dataSource.filteredData.length"
          [pageSize]="10"
          [pageSizeOptions]="[5, 10, 25, 50]"
          [showFirstLastButtons]="true"
        ></mat-paginator>
      </div>
    </div>
  </div>
</div>
